import React, { ChangeEvent, useCallback, useMemo } from 'react';
import Input from '@app/react-common/components/input/Input';
import TextArea from '@app/react-common/components/text-area/TextArea';
import Dropdown, { Option } from '@app/react-common/components/dropdown/Dropdown';
import Button from '@app/react-common/components/button/Button';
import Link from '@app/react-common/components/link/Link';
import { AbTestOptimizationTarget } from '@pdqdev/pdq-shared/dist/entities';
import { AbTestFormData } from './abTestModalUtils';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import { useAbTestingApi } from '../../../api/useAbTestingApi';

interface AbTestModalDetailsTabProps {
  formData: AbTestFormData;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  showInvalidStep: number;
  statisticalSignificanceError: string | undefined;
  setStatisticalSignificanceError: (error: string | undefined) => void;
  setFormDataValue: (name: string, value: any) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => void;
  onClose: () => void;
  getNextTab: (currentTabName: string) => string | null;
  handleTabChange: (tab: string) => void;
  handleDetailsValidation: (nextTab: string) => void;
  isThemeTest?: boolean;
  hasThemePermission?: boolean;
  isCrossSite?: boolean;
  hideCheckoutsLimit?: boolean;
}

export const AbTestModalDetailsTab: React.FC<AbTestModalDetailsTabProps> = React.memo(
  ({
    formData,
    mode,
    showInvalidStep,
    statisticalSignificanceError,
    setStatisticalSignificanceError,
    setFormDataValue,
    handleInputChange,
    onClose,
    getNextTab,
    handleTabChange,
    handleDetailsValidation,
    isThemeTest = false,
    hasThemePermission,
    isCrossSite = false,
    hideCheckoutsLimit = false,
  }) => {
    const abTestingApi = useAbTestingApi(false);

    const handleReinstallClick = useCallback(async () => {
      try {
        await abTestingApi.post('/ab-tests/permissions/themes/invalidate', {});
        window.open('https://admin.shopify.com/settings/apps', '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Failed to invalidate theme permission cache:', error);
        window.open('https://admin.shopify.com/settings/apps', '_blank', 'noopener,noreferrer');
      }
    }, [abTestingApi]);

    const handleNextClick = useCallback(() => {
      const nextTab = getNextTab('Details');
      if (nextTab) {
        if (mode === 'view') {
          handleTabChange(nextTab);
        } else {
          if (nextTab === 'Rules') {
            handleDetailsValidation(nextTab);
          } else {
            handleTabChange(nextTab);
          }
        }
      }
    }, [getNextTab, mode, handleTabChange, handleDetailsValidation]);

    // Memoize optimization target options to avoid recreating on every render
    const optimizationTargetOptions = useMemo(
      () => Object.entries(AbTestOptimizationTarget).map((entry) => ({ value: entry[0], label: entry[1] })),
      [],
    );

    const noThemePermissions = mode === 'create' && isCrossSite && !hasThemePermission;
    const isInputDisabled = mode === 'view' || noThemePermissions;

    return (
      <>
        {isCrossSite && !hasThemePermission && mode === 'create' && (
          <div style={{ marginBottom: '16px' }}>
            <WarningBanner
              variant="attention"
              hideCloseButton
              title="One-Time Setup Required"
              content={
                <>
                  Cross-Site Tests need permissions that weren't part of your original install. Just{' '}
                  <Link label="reinstall the app" onClick={handleReinstallClick} variant="regular" /> then refresh this
                  page to continue.
                </>
              }
            />
          </div>
        )}
        <Input
          disabled={isInputDisabled}
          type="text"
          placeholder='Give your test a short, clear name (e.g. "Free shipping vs Paid")'
          wrapperDivClassName="ab-test-modal__input"
          label="Test name"
          name="name"
          required={true}
          value={formData.name}
          onChange={handleInputChange}
          showValidation={showInvalidStep == 1}
        />
        <TextArea
          disabled={isInputDisabled}
          placeholder='What do you expect to learn? (e.g. "Free shipping will increase checkout rate.")'
          label="Hypothesis"
          name="description"
          required={true}
          wrapperDivClassName="ab-test-modal__input"
          value={formData.description}
          onChange={handleInputChange}
          showValidation={showInvalidStep == 1}
          ignoreWhitespace={true}
          minLength={100}
          maxLength={1000}
          invalidMessage="❌ Hypothesis must be at least 100 non-space characters. Please provide a clear explanation of what you are testing and why."
        />
        {!isThemeTest && !hideCheckoutsLimit && (
          <Input
            disabled={isInputDisabled}
            type="text"
            placeholder="Leave blank for unlimited"
            wrapperDivClassName="ab-test-modal__input"
            label="End test after X checkouts"
            name="numberOfCheckouts"
            value={formData.numberOfCheckouts || ''}
            onChange={handleInputChange}
            min={0}
          />
        )}
        <Dropdown
          disabled={isInputDisabled}
          label="Goal metric"
          options={optimizationTargetOptions}
          onSelect={(option: Option) => {
            setFormDataValue('optimizingFor', option.value);
          }}
          preSelectedOption={{
            label: AbTestOptimizationTarget[formData.optimizingFor],
            value: formData.optimizingFor,
          }}
        />

        <div className="ab-test-modal__actions">
          <Button
            onClick={onClose}
            text={'Cancel'}
            size={'medium'}
            variant={'secondary'}
            className="ab-test-modal__actions__cancel"
          />
          <Button
            onClick={handleNextClick}
            text={'Next'}
            size={'medium'}
            variant={'primary'}
            disabled={noThemePermissions}
          />
        </div>
      </>
    );
  },
);
